import { CertificateTypeEnum } from '@/generated/graphql/types';
import useDiplomasTranslation from '@/shared/components/Diplomas/hooks/useDiplomasTranslations';

import type {
  MyDiplomasCustom,
  TFilterDiplomasCollaboratorOption,
} from '../../type';
import { useLearningUserListCertificatesQueryQuery } from './get-list-certificates-collaborator-query.v5.generated';

const PAGE_SIZE = 12;

export const useGetListCertificatesCollaborator = ({
  filterByTypeCertificate,
  currentPage,
}: {
  filterByTypeCertificate?: TFilterDiplomasCollaboratorOption;
  currentPage: number;
}) => {
  const { t } = useDiplomasTranslation();
  const { data, loading, error, refetch } =
    useLearningUserListCertificatesQueryQuery({
      variables: {
        input: {
          certificate_type_filter_enum:
            filterByTypeCertificate === 'ALL'
              ? undefined
              : filterByTypeCertificate,
          first: PAGE_SIZE,
          page: currentPage,
        },
      },
      context: {
        clientName: 'v5',
      },
    });

  const certificatesMetadata =
    data?.certificate_collaborator?.get_certificates?.metadata;
  const totalPages =
    data?.certificate_collaborator?.get_certificates?.page_info?.total_pages;

  const labelByTypeCertificate: Record<CertificateTypeEnum, string> = {
    [CertificateTypeEnum.CertificateTypeCourse]: t('CERTIFICATE'),
    [CertificateTypeEnum.CertificateTypeTrack]: t('CERTIFICATE'),
    [CertificateTypeEnum.CertificateTypeAcademy]: t('CERTIFICATE'),
    [CertificateTypeEnum.CertificateTypeCareer]: t('CERTIFICATE'),
    [CertificateTypeEnum.CertificateTypeCertification]: t('CERTIFICATE'),
    [CertificateTypeEnum.CertificateTypeLiveSession]: t('CONSTANCE'),
    [CertificateTypeEnum.ParticipationCertificateTypeCourse]: t('CONSTANCE'),
    [CertificateTypeEnum.ParticipationCertificateTypeMinedu]: t('CONSTANCE'),
    [CertificateTypeEnum.ParticipationCertificateTypeSixthAnniversary]:
      t('CONSTANCE'),
    [CertificateTypeEnum.ParticipationCertificateTypeWorkshop]: t('CONSTANCE'),
  };

  const listCertificatesCollaborator: MyDiplomasCustom[] =
    data?.certificate_collaborator?.get_certificates?.edges?.map(
      certificate => {
        // Optional chaining kept defensively even though `node` is typed as
        // non-nullable — guards against a malformed/partial API response.
        const node = certificate?.node;

        const isCertificateTrack =
          node?.certificate_type === CertificateTypeEnum.CertificateTypeTrack;
        const isCertificateCourse =
          node?.certificate_type === CertificateTypeEnum.CertificateTypeCourse;
        const isConstanceParticipationCourse =
          node?.certificate_type ===
          CertificateTypeEnum.ParticipationCertificateTypeCourse;
        const isLiveSessionCertificate =
          node?.certificate_type ===
          CertificateTypeEnum.CertificateTypeLiveSession;
        const isConstanceOrLiveSession =
          isConstanceParticipationCourse || isLiveSessionCertificate;

        return {
          enrollment:
            isCertificateCourse || isConstanceOrLiveSession
              ? {
                  course: {
                    title: node?.title || '',
                    originalId: node?.id,
                    dashboardUrl: isConstanceOrLiveSession
                      ? (node?.attachment_url ?? null)
                      : '',
                    promoImage: node?.image_url ?? null,
                    __typename: 'CourseNode',
                  },
                  __typename: 'EnrollmentNode',
                }
              : undefined,
          enrollmentTrack: isCertificateTrack
            ? {
                track: {
                  originalId: node?.id,
                  title: node?.title || '',
                  promoImage: node?.image_url ?? null,
                },
              }
            : undefined,
          attachmentUrl: node?.attachment_url,
          diplomaUrl: isConstanceOrLiveSession
            ? node?.attachment_url
            : `/diplomas/${node?.code}`,
          code: node?.code,
          createdAt: node?.created_date,
          id: node?.id,
          createdDate: node?.created_date,
          enrollmentAcademySkillpath: null,
          enrollmentBundle: null,
          enrollmentCareer: null,
          imageThumbnailUrl: node?.image_url,
          originalId: node?.id,
          type: labelByTypeCertificate[
            node?.certificate_type || CertificateTypeEnum.CertificateTypeCourse
          ],
        };
      },
    ) || [];

  return {
    certificatesMetadata,
    listCertificatesCollaborator,
    totalPages,
    loading,
    error,
    refetch,
  };
};
